import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BarcodeGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/barcode-generator',
			name: 'Barcode Generator — Create Scannable Barcodes Fast with Lift',
			description:
				'Generate a barcode for EAN, UPC, Code 128, Code 39, ITF-14, or ISBN in seconds. Try Lift now!',
			url: 'https://liftapp.ai/barcode-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/barcode-generator#product' },
				{ '@id': 'https://liftapp.ai/barcode-generator#howto' },
				{ '@id': 'https://liftapp.ai/barcode-generator#benefits' },
				{ '@id': 'https://liftapp.ai/barcode-generator#faq' },
				{ '@id': 'https://liftapp.ai/barcode-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/barcode-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/barcode-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/barcode-generator#product',
			name: 'Barcode Generator',
			description:
				'Turn product codes, book numbers, or custom text into a scannable barcode with Lift. Pick a barcode type, enter your data, and download a ready-to-print image in seconds.',
			url: 'https://liftapp.ai/barcode-generator',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/barcode-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Choose your barcode type',
					text: 'Select the barcode format you need and switch tabs without losing your entries.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Enter your barcode data',
					text: 'Type in the digits or text your chosen format requires, and Lift checks the length and characters as you go.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Generate your barcode',
					text: 'Tap Generate and Lift turns your data into a clean, scannable barcode in moments.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download, adjust, or regenerate',
					text: 'Toggle the number display, download the barcode as an image, or tap Regenerate anytime your details change.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/barcode-generator#benefits',
			name: 'Who can benefit from the Lift Barcode Generator?',
			description:
				"Lift's Barcode Generator is useful for e-commerce sellers, authors, small business owners, warehouse teams, and packaging designers who need valid, scannable barcodes.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'E-commerce sellers — Get a valid product code for a marketplace listing or price tag. Enter your number on the UPC-A or EAN-13 tab and download a barcode ready for your storefront or packaging.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: "Authors & self-publishers — Turn your book's ISBN into the barcode required on a back cover. Enter it on the ISBN tab and download a print-ready barcode.",
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business & retail owners — Label your own products or shelves with a custom SKU. Enter your code on the Code 128 or Code 39 tab and print the barcode straight onto packaging or price tags.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Warehouse & shipping teams — Mark cartons and pallets for fast, reliable scanning. Enter your case code on the ITF-14 tab and generate a barcode built for high-volume handling.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Packaging & label designers — Drop a real, working barcode into a mockup before it goes to print. Enter any code on the tab matching your target format and download it instantly.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Barcode Generator by Lift',
				description:
					'Example of how Lift Barcode Generator turns product codes, book numbers, and custom text into scannable barcodes.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/barcode-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Barcode Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift Barcode Generator turns product codes, book numbers, or custom text into a scannable barcode, including EAN-13, EAN-8, UPC-A, UPC-E, Code 128, Code 39, ITF-14, and ISBN.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Barcode Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Choose a barcode format, enter the required digits or text, and tap Generate to create your barcode. Once it's ready, download the image or tap Regenerate to make a new version.",
					},
				},
				{
					'@type': 'Question',
					name: 'What barcode formats can I create with Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift supports several barcode formats — EAN-13, EAN-8, UPC-A, UPC-E, Code 128, Code 39, ITF-14, and ISBN. Each format has its own field rules, so your barcode is valid for the standard you need.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I edit or regenerate my barcode after creating it?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Switch back to any tab, update your details, and tap Regenerate to create a new barcode instantly. Your entries stay saved so you can adjust and try again.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to enter my product or barcode data in Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes the information you enter, including product codes and text, securely and uses it only to generate your barcode.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/barcode-generator#why-lift',
			name: 'Why Choose the Lift Barcode Generator',
			description:
				"Lift's Barcode Generator combines high-quality results, intelligent rendering, cross-platform access, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift Barcode Generator',
				description:
					"Comparison of Lift's Barcode Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/barcode-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/barcode-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Barcode Generator',
					item: 'https://liftapp.ai/barcode-generator',
				},
			],
		},
	],
};
